/* ============================================================================
   ANCHORS                                          (SITE-RULES 2)

   An anchor is a NAMED, TWO-WAY relationship. A member can be *assigned* a support
   coach and still be unknown to everyone in the building — that gap is the product.
   So every anchor carries `basis` (what actually makes it reciprocal, in the words of
   the person recording it) and `lastTwoWay` (when it was last actually reciprocal).

   THREE STATES, NEVER A BOOLEAN. No anchor, a decaying anchor and a strong anchor are
   three different problems needing three different moves, and collapsing the first two
   is how "nobody knows her" and "somebody knew her in June" became the same row.
   Nothing here ever DELETES an anchor to say it went quiet.

   Six screens read `m.anchors`; until 11 Sep 2026 nothing could write one, so the
   product's central idea was seed data only. `makeAnchor()` is the write path, and it
   refuses an anchor with no basis, because that is an assignment wearing an anchor's
   name.
   ============================================================================ */
import { ANCHOR_DECAY_DAYS } from './types.js';
import { daysAgoISO, daysSince, todayISO } from './time.js';
/**
 * DERIVED from recency, never a stored opinion.
 *
 * util.js: `makeAnchor()` and `touchMemberAnchor()` both wrote `strength: 'strong'` and
 * nothing ever wrote it back down, so an anchor recorded through the UI stayed strong
 * forever and never aged into the score. The stored field is not read here and is not
 * carried on `Anchor` in types.ts at all. `lastTwoWay` is the only input.
 *
 * The cut-off is ANCHOR_DECAY_DAYS = 30, from SITE-RULES 2's "a month". Three different
 * answers were once live at once — util.js said 45, the seed computed at 21, and the two
 * disagreed on 102 of 403 seeded anchors.
 */
export function anchorStrength(a, today = todayISO()) {
    if (!a)
        return 'weak';
    const d = daysSince(a.lastTwoWay, today);
    if (d == null)
        return 'weak';
    return d <= ANCHOR_DECAY_DAYS ? 'strong' : 'weak';
}
/**
 * The middle state. Decaying is NOT the absence of an anchor: the move is different
 * (rekindle a relationship that exists, rather than build one that does not), which is
 * why `riskModel` deducts half for it and the whole family for no anchor at all.
 *
 * An anchor with no `lastTwoWay` is `weak` but not `decaying`: there is nothing on
 * record to have decayed. This mirrors util.js exactly.
 */
export function anchorIsDecaying(a, today = todayISO()) {
    const d = daysSince(a?.lastTwoWay, today);
    return d != null && d > ANCHOR_DECAY_DAYS;
}
/**
 * Record a real relationship. Returns the anchor, or NULL if it was refused.
 *
 * `basis` is required by rule 2: without it this is an assignment wearing an anchor's
 * name, and the whole model degrades to attendance tracking — the thing Acrostic argues
 * against being. The DDL in MIGRATION.md §4 enforces the same thing with
 * `check (length(btrim(basis)) > 0)`; this is that check, on the way in.
 *
 * `lastTwoWay` starts at today because recording an anchor IS a two-way moment: somebody
 * sat down and said who knows this person. It decays from there like any other.
 */
export function makeAnchor(input, today = todayISO()) {
    const name = String(input?.name ?? '').trim();
    const basis = String(input?.basis ?? '').trim();
    if (!name || !basis)
        return null;
    const kind = input.kind ?? 'staff';
    const anchor = {
        name,
        kind,
        role: input.role ?? (kind === 'member' ? 'Trains with them' : 'Support coach'),
        basis,
        since: input.since ?? today,
        lastTwoWay: today,
    };
    if (input.inherited)
        anchor.inherited = true;
    if (input.staffId)
        anchor.staffId = input.staffId;
    if (input.otherMemberId)
        anchor.otherMemberId = input.otherMemberId;
    return anchor;
}
/**
 * The strongest anchor — what the guidance means by "from the strongest
 * existing anchor". Strong beats weak; among equals, the most recent two-way wins.
 */
export function primaryAnchor(m, today = todayISO()) {
    const list = m?.anchors ?? [];
    if (!list.length)
        return null;
    return list
        .slice()
        .sort((a, b) => {
        const s = Number(anchorStrength(b, today) === 'strong') - Number(anchorStrength(a, today) === 'strong');
        if (s)
            return s;
        return String(b.lastTwoWay ?? '').localeCompare(String(a.lastTwoWay ?? ''));
    })[0] ?? null;
}
/**
 * Everyone who could plausibly be an anchor for this member: the staff on the roster,
 * plus the members they actually train beside (same session, recently). A
 * member-to-member anchor is the strongest kind the product has, so it must be as easy
 * to record as a staff one — and it is DERIVED from attendance, not invented.
 */
export function anchorCandidates(db, m, limit) {
    const out = [];
    const have = new Set((m.anchors ?? []).map(a => a.name));
    for (const u of (db.users ?? [])) {
        const n = u.name;
        if (n && !have.has(n))
            out.push({ name: n, kind: 'staff', role: roleLabel(u) });
    }
    // Same room, same day: the only evidence the product actually holds.
    const mine = new Set((m.visits ?? []).slice(0, 40).map(sessionSlot));
    const seen = new Set();
    for (const o of db.members ?? []) {
        if (o.id === m.id || have.has(o.name) || seen.has(o.name))
            continue;
        if ((o.visits ?? []).some(v => mine.has(sessionSlot(v)))) {
            seen.add(o.name);
            out.push({ name: o.name, kind: 'member', role: 'Trains with them', id: o.id });
        }
    }
    return limit ? out.slice(0, limit) : out;
}
function sessionSlot(v) {
    return v.date + '|' + (v.className ?? '');
}
function roleLabel(u) {
    const r = u.roles?.[0];
    if (!r)
        return 'Staff';
    return r === 'sales' ? 'Support coach'
        : r === 'coach' ? 'Coach'
            : r === 'manager' ? 'Manager'
                : 'Staff';
}
/* ── Rewinding an anchor ─────────────────────────────────────────────────── */
/**
 * The anchors this member had as at `asOf`, as they stood then.
 *
 * An anchor that did not exist yet is dropped. One whose `lastTwoWay` is AFTER `asOf`
 * existed, but the last reciprocal contact as at that date is unknown, and util.js's
 * answer — the conservative one — is that it reads weak. Expressed here by clamping
 * `lastTwoWay` to a date that is definitively stale as at `asOf`, so strength stays
 * DERIVED (util.js stamped `strength: 'weak'` onto a copy, a field types.ts does not
 * carry and `anchorStrength` would not have read anyway).
 *
 * An anchor that simply went quiet before `asOf` is returned untouched:
 * `anchorStrength(a, asOf)` already answers that correctly.
 */
export function anchorsAt(m, asOf) {
    return (m?.anchors ?? [])
        .filter(a => !a.since || a.since <= asOf)
        .map(a => (!a.lastTwoWay || a.lastTwoWay > asOf)
        ? { ...a, lastTwoWay: daysAgoISO(ANCHOR_DECAY_DAYS + 1, asOf) }
        : a);
}
